import React, { FunctionComponent, useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { Category, CategoryId } from "../../models/Product";
import { Language } from "../../models/Translation";
import { thunkFetchProductCategories } from "../../store/products/action";
import { thunkFetchCurrentTenantLanguages } from "../../store/tenants/actions";
import { useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";

interface CategorySelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected?: (value: Category) => void;
  variant?: "filled" | "outlined" | "standard";
  value?: CategoryId;
  error?: boolean;
}

export const CategorySelect: FunctionComponent<CategorySelectProps> = ({
  label = "Category",
  onSelected,
  onChange,
  variant = "standard",
  value,
  error = false,
  ...props
}) => {
  const dispatch = useDispatch();
  const tenantLanguages =
    useSelector<AppState, ReadonlyArray<Language>>(
      s => s.currentTenant.languages
    ) || [];
  const categories =
    useSelector<AppState, ReadonlyArray<Category>>(
      s => s.storeClusterProducts.categories
    ) || [];
  const [state, setState] = useState<ReadonlyArray<Category>>([]);

  useEffect(() => {
    if (!categories.length) {
      dispatch(thunkFetchProductCategories());
    }
  }, []);

  useLoadIfEmpty(!tenantLanguages.length, thunkFetchCurrentTenantLanguages);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let category = state.find(c => c.id === event.target.value);
      if (category) {
        onSelected(category);
      }
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  useEffect(() => {
    let filtered =
      categories.length > 0 &&
      categories.filter(c => (value === "" ? true : c.id !== ""));

    if (filtered) {
      setState(
        [...filtered].sort((a: any, b: any) => {
          if (
            a.translation === undefined ||
            b.translation === undefined ||
            a.translation.values[tenantLanguages[0].code] === undefined ||
            b.translation.values[tenantLanguages[0].code] === undefined
          )
            return a.name.localeCompare(b.name);

          return a.translation.values[tenantLanguages[0].code].localeCompare(
            b.translation.values[tenantLanguages[0].code]
          );
        })
      );
    }
  }, [categories]);

  return (
    <FormControl
      disabled={props.disabled}
      required={props.required}
      variant={variant}
      className={props.className}
      error={error}
    >
      <InputLabel
        id="category-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        value={value}
        onChange={handleChange}
        labelId="category-select-label"
        id="category-select"
        autoWidth={true}
      >
        {state &&
          state.length > 0 &&
          state.map(x => (
            <MenuItem key={x.id} value={x.id}>
              {x.translation?.values !== undefined
                ? x.translation.values[tenantLanguages[0].code]
                : x.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
